import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Swords, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Competitors() {
  const { t } = useTranslation(["competitors", "common", "errors"]);
  const [competitors, setCompetitors] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState("__all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, bRes] = await Promise.all([api.get("/competitors"), api.get("/brands")]);
      setCompetitors(cRes.data);
      setBrands(bRes.data);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const brandsById = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b])), [brands]);
  const filtered = useMemo(
    () => (brandFilter === "__all" ? competitors : competitors.filter((c) => c.brand_id === brandFilter)),
    [competitors, brandFilter]
  );

  return (
    <div className="space-y-8" data-testid="competitors-page">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <Swords className="mr-1 inline h-3 w-3" strokeWidth={2} /> EvidenceLab
          </span>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{t("competitors:list.title")}</h1>
          <p className="max-w-xl text-sm text-slate-500">{t("competitors:list.subtitle")}</p>
        </div>
        <Select value={brandFilter} onValueChange={setBrandFilter}>
          <SelectTrigger data-testid="competitors-brand-filter" className="h-10 w-56">
            <SelectValue placeholder={t("competitors:list.filterByBrand")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">{t("competitors:list.filterByBrand")}</SelectItem>
            {brands.map((b) => (
              <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </header>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="competitors-table-container">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-16 text-sm text-slate-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("common:loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="px-8 py-14 text-center" data-testid="competitors-empty">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
              <Swords className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <h3 className="font-display text-lg font-semibold text-slate-900">{t("competitors:list.empty.title")}</h3>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{t("competitors:list.empty.body")}</p>
            <Button asChild className="mt-5 bg-slate-900 text-white hover:bg-slate-800">
              <Link to="/brands">{t("competitors:list.empty.cta")}</Link>
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-b border-slate-200 bg-slate-50">
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("competitors:list.columns.name")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("competitors:list.columns.brand")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("competitors:list.columns.relation")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("competitors:list.columns.confidence")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("competitors:list.columns.discovered")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`competitor-row-${c.id}`} className="border-slate-100">
                  <TableCell className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-slate-900">{c.name}</span>
                      {c.url && (
                        <a href={c.url} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-indigo-600">
                          {c.url.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-slate-600">
                    {brandsById[c.brand_id] ? (
                      <Link to={`/brands/${c.brand_id}`} className="text-indigo-700 hover:text-indigo-900">{brandsById[c.brand_id].name}</Link>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-slate-600">{t(`relation.${c.relation_type}`)}</TableCell>
                  <TableCell className="px-6 py-4 text-sm text-slate-600">{Math.round((c.confidence || 0) * 100)}%</TableCell>
                  <TableCell className="px-6 py-4">
                    <Badge variant="outline" className={c.discovered_by === "ai_suggestion" ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-slate-50 text-slate-700"}>
                      {c.discovered_by === "ai_suggestion" && <Sparkles className="mr-1 h-2.5 w-2.5" strokeWidth={2} />}
                      {t(`discovered.${c.discovered_by}`)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
